import logo from '../assets/logo_ekynair.png';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../constants/theme';
import { mainLine, tooltipTitle } from '../shared/buildInfo';

const ResetPassword = ({ onSuccess, darkMode }) => {
  const { t } = useTranslation();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    // Extraire le token de l'URL
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      setToken(urlToken);
    } else {
      setMessage({ type: 'error', text: t('auth.reset.msg.missingToken') });
    }
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: t('signup.messages.passwordMismatch') });
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: t('auth.reset.msg.success') });
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        setMessage({ type: 'error', text: `${data.message}` });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('auth.reset.msg.error') });
    } finally {
      setLoading(false);
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    background: darkMode ? '#0f172a' : '#f8f9fa',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  };

  const formStyle = {
    background: darkMode ? '#1E1E32' : '#fff',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: darkMode ? '0 10px 40px rgba(0, 0, 0, 0.5)' : '0 10px 40px rgba(0, 0, 0, 0.1)',
    maxWidth: '450px',
    width: '100%'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '6px',
    border: darkMode ? '1px solid rgba(77, 198, 255, 0.3)' : '1px solid #ddd',
    background: darkMode ? '#34495E' : '#fff',
    color: darkMode ? '#E1E1E1' : '#2C3E50',
    fontSize: '14px',
    marginBottom: '15px'
  };

  const buttonStyle = {
    width: '100%',
    padding: '12px',
    background: loading ? '#999' : BRAND.gradient,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: loading ? 'not-allowed' : 'pointer',
    marginTop: '10px'
  };

  return (
    <div style={containerStyle}>
      <div style={formStyle}>
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <img src={logo} alt="EkynAir" style={{ height: '80px' }} />
        </div>
        <h2 style={{
          color: darkMode ? '#E1E1E1' : '#2C3E50',
          marginBottom: '10px',
          fontSize: '20px',
          textAlign: 'center',
          fontFamily: "'Poppins', sans-serif"
        }}>
          {t('auth.reset.title')}
        </h2>

        <p style={{
          color: darkMode ? '#E8EDF2' : '#4A5568',
          fontSize: '14px',
          textAlign: 'center',
          marginBottom: '30px'
        }}>
          {t('auth.reset.subtitle')}
        </p>

        {message && (
          <div style={{
            padding: '12px',
            borderRadius: '6px',
            marginBottom: '20px',
            background: message.type === 'success' 
              ? (darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)')
              : (darkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)'),
            color: message.type === 'success'
              ? (darkMode ? '#81C784' : '#388E3C')
              : (darkMode ? '#EF5350' : '#D32F2F'),
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '15px', position: 'relative' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: darkMode ? '#E8EDF2' : '#4A5568',
              fontSize: '14px'
            }}>
              {t('auth.reset.newPassword')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('auth.reset.newPasswordPlaceholder')}
                required
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '12px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                {showPassword ? t('signup.password.toggle.hide') : t('signup.password.toggle.show')}
              </button>
            </div>
            <div style={{
              fontSize: '12px',
              color: darkMode ? '#E8EDF2' : '#4A5568',
              marginTop: '5px'
            }}>
              {t('auth.reset.hint')}
            </div>
          </div>

          <div style={{ marginBottom: '15px', position: 'relative' }}>
            <label style={{
              display: 'block',
              marginBottom: '5px',
              color: darkMode ? '#E8EDF2' : '#4A5568',
              fontSize: '14px'
            }}>
              {t('signup.fields.confirmPassword.label')} *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.reset.confirmPlaceholder')}
                required
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '12px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px'
                }}
              >
                {showConfirm ? t('signup.password.toggle.hide') : t('signup.password.toggle.show')}
              </button>
            </div>
          </div>

          <button type="submit" style={buttonStyle} disabled={loading}>
            {loading ? t('auth.reset.submit.loading') : t('auth.reset.submit.idle')}
          </button>
        </form>

        <div title={tooltipTitle} style={{
          textAlign: 'center',
          paddingTop: '20px',
          fontSize: '12px',
          color: darkMode ? '#E8EDF2' : '#4A5568'
        }}>
          {mainLine}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
